/**
 * SQLite Dependency Service for Browser Mode
 *
 * Ports the desktop SQLite dependency logic to work with sql.js in the browser.
 * Provides foreign key and view dependency tracking for tables and views.
 */

import type { Database } from 'sql.js';
import { getCurrentDatabase } from './sqliteManager';

export type DependencyObjectType =
  | 'table'
  | 'view'
  | 'procedure'
  | 'function'
  | 'trigger'
  | 'index'
  | 'constraint'
  | 'unknown';

export type DependencyItem = {
  name: string;
  schema: string;
  objectType: DependencyObjectType;
  columnName?: string;
};

export type DependencyData = {
  uses: DependencyItem[];
  usedBy: DependencyItem[];
};

export type GetDependenciesResult = {
  success: boolean;
  dependencies?: DependencyData;
  error?: string;
  limitedSupport?: boolean;
};

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Get foreign keys that this table references (outgoing FK references)
 * This answers: "What tables does this table depend on?"
 */
export function getForeignKeyDependencies(
  db: Database,
  tableName: string
): DependencyItem[] {
  try {
    const escapedName = tableName.replace(/"/g, '""');
    const result = db.exec(`PRAGMA foreign_key_list("${escapedName}")`);

    if (result.length === 0 || result[0].values.length === 0) {
      return [];
    }

    const columns = result[0].columns;
    const rows = result[0].values;

    // Parse column indices
    const tableIdx = columns.indexOf('table');

    // Group by referenced table (a single FK can span multiple columns)
    const tableMap = new Map<string, DependencyItem>();
    for (const row of rows) {
      const referencedTable = row[tableIdx] as string;
      if (!tableMap.has(referencedTable)) {
        tableMap.set(referencedTable, {
          name: referencedTable,
          schema: '', // SQLite doesn't have schemas
          objectType: 'table',
        });
      }
    }

    return Array.from(tableMap.values());
  } catch (error) {
    console.error('[SQLite Browser] Error fetching foreign keys:', error);
    return [];
  }
}

/**
 * Get tables that have foreign keys pointing to this table (incoming FK references)
 * This answers: "What tables depend on this table via FK?"
 */
export function getIncomingForeignKeyDependencies(
  db: Database,
  tableName: string
): DependencyItem[] {
  try {
    // Get all tables
    const tablesResult = db.exec(
      `SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%'`
    );

    if (tablesResult.length === 0) {
      return [];
    }

    const tables = tablesResult[0].values.map((row) => row[0] as string);
    const dependentTables: DependencyItem[] = [];

    for (const tableRow of tables) {
      if (tableRow === tableName) continue; // Skip self

      const escapedName = tableRow.replace(/"/g, '""');
      const fkResult = db.exec(`PRAGMA foreign_key_list("${escapedName}")`);

      if (fkResult.length === 0 || fkResult[0].values.length === 0) {
        continue;
      }

      const columns = fkResult[0].columns;
      const tableIdx = columns.indexOf('table');
      const rows = fkResult[0].values;

      // Check if any FK references our target table
      const referencesTarget = rows.some(
        (row) => (row[tableIdx] as string).toLowerCase() === tableName.toLowerCase()
      );

      if (referencesTarget) {
        dependentTables.push({
          name: tableRow,
          schema: '',
          objectType: 'table',
        });
      }
    }

    return dependentTables;
  } catch (error) {
    console.error('[SQLite Browser] Error fetching incoming foreign keys:', error);
    return [];
  }
}

/**
 * Find views that reference a given table by parsing their SQL definitions
 * This is a simple pattern match - not a full SQL parser
 */
export function getViewDependencies(
  db: Database,
  tableName: string
): DependencyItem[] {
  try {
    const result = db.exec(
      `SELECT name, sql FROM sqlite_master WHERE type = 'view' AND sql IS NOT NULL`
    );

    if (result.length === 0) {
      return [];
    }

    const views = result[0].values;
    const dependentViews: DependencyItem[] = [];

    // Create regex patterns for common table reference patterns
    const patterns = [
      new RegExp(`\\bFROM\\s+["'\`]?${escapeRegex(tableName)}["'\`]?\\b`, 'i'),
      new RegExp(`\\bJOIN\\s+["'\`]?${escapeRegex(tableName)}["'\`]?\\b`, 'i'),
      new RegExp(`\\bINTO\\s+["'\`]?${escapeRegex(tableName)}["'\`]?\\b`, 'i'),
      new RegExp(`\\bUPDATE\\s+["'\`]?${escapeRegex(tableName)}["'\`]?\\b`, 'i'),
    ];

    for (const row of views) {
      const viewName = row[0] as string;
      const sql = row[1] as string | null;

      if (!sql) continue;

      const matches = patterns.some((pattern) => pattern.test(sql));

      if (matches) {
        dependentViews.push({
          name: viewName,
          schema: '',
          objectType: 'view',
        });
      }
    }

    return dependentViews;
  } catch (error) {
    console.error('[SQLite Browser] Error fetching view dependencies:', error);
    return [];
  }
}

/**
 * Find tables that a view references by parsing its SQL definition
 */
export function getViewUsedTables(
  db: Database,
  viewName: string
): DependencyItem[] {
  try {
    const result = db.exec(
      `SELECT sql FROM sqlite_master WHERE type = 'view' AND name = '${viewName.replace(/'/g, "''")}' AND sql IS NOT NULL`
    );

    if (result.length === 0 || result[0].values.length === 0) {
      return [];
    }

    const sql = result[0].values[0][0] as string | null;
    if (!sql) return [];

    // Get all table names
    const tablesResult = db.exec(
      `SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%'`
    );

    if (tablesResult.length === 0) {
      return [];
    }

    const tables = tablesResult[0].values.map((row) => row[0] as string);
    const usedTables: DependencyItem[] = [];

    for (const table of tables) {
      const patterns = [
        new RegExp(`\\bFROM\\s+["'\`]?${escapeRegex(table)}["'\`]?\\b`, 'i'),
        new RegExp(`\\bJOIN\\s+["'\`]?${escapeRegex(table)}["'\`]?\\b`, 'i'),
      ];

      if (patterns.some((p) => p.test(sql))) {
        usedTables.push({
          name: table,
          schema: '',
          objectType: 'table',
        });
      }
    }

    return usedTables;
  } catch (error) {
    console.error('[SQLite Browser] Error parsing view dependencies:', error);
    return [];
  }
}

/**
 * Fetch dependencies for a table or view in SQLite (browser mode)
 */
export function getSqliteDependencies(
  objectName: string,
  objectType: 'table' | 'view'
): GetDependenciesResult {
  const db = getCurrentDatabase();
  if (!db) {
    return {
      success: false,
      error: 'No SQLite database is currently open',
    };
  }

  try {
    if (objectType === 'table') {
      // For tables:
      // - "uses" = tables this table has FK references to
      // - "usedBy" = tables with FK to this table + views referencing this table
      const uses = getForeignKeyDependencies(db, objectName);
      const fkDependents = getIncomingForeignKeyDependencies(db, objectName);
      const viewDependents = getViewDependencies(db, objectName);

      return {
        success: true,
        dependencies: {
          uses,
          usedBy: [...fkDependents, ...viewDependents],
        },
        limitedSupport: true,
      };
    }

    if (objectType === 'view') {
      // For views:
      // - "uses" = tables/views this view references
      // - "usedBy" = other views referencing this view
      const uses = getViewUsedTables(db, objectName);
      const usedBy = getViewDependencies(db, objectName);

      return {
        success: true,
        dependencies: {
          uses,
          usedBy,
        },
        limitedSupport: true,
      };
    }

    // Other types not supported in SQLite
    return {
      success: true,
      dependencies: { uses: [], usedBy: [] },
      limitedSupport: true,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
